"use client";
/**
 * useChat — core state machine for a single ASK conversation.
 * Track C2.
 */
import { useState, useCallback, useRef, useEffect } from "react";
import type { Dispatch, SetStateAction } from "react";
import { useAuthContext } from "@/platform/auth/AuthContext";
import { createSession, getSession, updateSession, canCreateSession } from "@/platform/firebase/firestore/ask";
import { sendMessage as apiSendMessage } from "@/modes/ask/api";
import { logAskSessionStarted, logAskMessageSent, logAskMessageReceived } from "@/platform/firebase/firestore/adminEvents";
import { isPaidProfile, tokenLimitForPlan } from "@/shared/types";
import type { Message, Session } from "@/modes/ask/types";

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ChatError {
  message: string;
  retry:   () => void;
}

interface UseChatReturn {
  messages:        Message[];
  /** Update the local message list directly (used by inline edit). */
  setMessages:     Dispatch<SetStateAction<Message[]>>;
  isLoading:       boolean;
  error:           ChatError | null;
  sessionId:       string;
  sendMessage:     (content: string, attachments?: File[], mode?: "fast" | "expert") => Promise<void>;
  /** Edit-and-regenerate: replace the edited user message, drop the rest and re-run. */
  regenerateMessage: (message: Message) => Promise<void>;
  loadConversation:(sessionId: string) => Promise<void>;
  clearError:      () => void;
  clearSession:    () => void;
  /** Abort the in-flight request/stream (Stop button). */
  stop:            () => void;
  sessionsUsed:    number;
  sessionsLimit:   number;
  sessionLimitReached: boolean;
  showSessionLimit:    boolean;
  setShowSessionLimit: (v: boolean) => void;
}

// ─── Token limit helper ───────────────────────────────────────────────────────

function getTotalTokens(messages: Message[]): number {
  return messages.reduce((sum, m) => sum + (m.token_usage?.total_tokens ?? 0), 0);
}

function isAbortError(err: unknown): boolean {
  return err instanceof DOMException
    ? err.name === "AbortError"
    : (err as { name?: string } | null)?.name === "AbortError";
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useChat(
  initialSessionId?: string,
  options?: { onAutoNewChat?: (carriedText?: string) => void }
): UseChatReturn {
  const { onAutoNewChat } = options ?? {};
  const { user, profile, loading: authLoading } = useAuthContext();

  const [messages,  setMessages]  = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error,     setError]     = useState<ChatError | null>(null);
  const [sessionId, setSessionId] = useState<string>(initialSessionId ?? "");

  const [sessionLimitReached, setSessionLimitReached] = useState(false);
  const [showSessionLimit, setShowSessionLimit] = useState(false);
  const [sessionsUsed, setSessionsUsed] = useState(0);
  const [sessionsLimit, setSessionsLimit] = useState(5);

  const sessionIdRef = useRef(sessionId);
  sessionIdRef.current = sessionId;

  const messagesRef = useRef(messages);
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  const abortRef = useRef<AbortController | null>(null);
  const requestInFlightRef = useRef(false);

  // Auto "new chat" after a session completes (token limit reached).
  const sessionResetTimerRef = useRef<number | null>(null);
  useEffect(() => () => {
    if (sessionResetTimerRef.current) window.clearTimeout(sessionResetTimerRef.current);
  }, []);

  // While set, the question the user typed at the token cap is re-submitted
  // automatically into the freshly reset conversation (auto-continue).
  const autoSendPendingRef = useRef<string | null>(null);

  // ── Load session count on mount for free users ───────────────────────────
  useEffect(() => {
    if (!user || !profile || authLoading) return;
    const isPro = isPaidProfile(profile.subscription);
    if (isPro) return;

    canCreateSession(user.uid).then((check) => {
      setSessionsUsed(check.sessionsUsed);
      setSessionsLimit(check.sessionsLimit);
      if (check.sessionsUsed >= check.sessionsLimit) {
        setSessionLimitReached(true);
      }
      if (!check.allowed) {
        setShowSessionLimit(true);
      }
    }).catch((err) => {
      // Non-fatal — never crash the page over the limit check.
      console.warn("[ask] session-limit check failed", err);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid, profile?.subscription?.tier, authLoading]);

  // ── loadConversation ──────────────────────────────────────────────────────

  const loadConversation = useCallback(async (convId: string) => {
    let session: Session | null = null;
    try {
      session = await getSession(convId);
    } catch (err) {
      // Not found or not owned by the signed-in user (e.g. a stale /ask/{id}
      // URL after switching accounts) — never crash over it.
      console.warn("[ask] cannot load conversation", convId, err);
    }
    if (!session) {
      setMessages([]);
      setSessionId("");
      if (typeof window !== "undefined") {
        window.history.replaceState(null, "", "/ask");
      }
      return;
    }

    const formatted: Message[] = (session.messages ?? []).map((m) => ({
      id:          m.id || Date.now().toString(),
      role:        m.role,
      content:     m.content,
      timestamp:   m.timestamp ?? new Date().toISOString(),
      attachments: m.attachments,
      suggestions: m.suggestions,
      token_usage: m.token_usage,
    }));

    setMessages(formatted);
    setSessionId(session.id);
  }, []);

  // ── clearSession ──────────────────────────────────────────────────────────

  const clearSession = useCallback(() => {
    setMessages([]);
    setSessionId("");
    setError(null);
  }, []);

  // ── clearError ────────────────────────────────────────────────────────────

  const clearError = useCallback(() => setError(null), []);

  // ── stop ────────────────────────────────────────────────────────────────────

  const stop = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  // ── runAssistantRequest ──────────────────────────────────────────────────
  // Shared request pipeline: appends a "Thinking…" placeholder, streams the
  // model response, finalises the assistant message and persists the session.
  // Used by sendMessage (new question) and regenerateMessage (edit → re-run).

  const runAssistantRequest = useCallback(
    async ({
      sessionId,
      userQuery,
      attachments,
      baseMessages,
      snapshotMessages,
      mode,
      isNewConversation = false,
      onError,
    }: {
      sessionId: string;
      userQuery: string;
      attachments?: File[];
      baseMessages: Message[];
      snapshotMessages: Message[];
      mode: "fast" | "expert";
      isNewConversation?: boolean;
      onError: (message: string) => void;
    }) => {
      if (requestInFlightRef.current) return;
      requestInFlightRef.current = true;
      setIsLoading(true);

      const assistantId = `assistant-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const placeholder: Message = {
        id:        assistantId,
        role:      "assistant",
        content:   "Thinking...",
        timestamp: new Date().toISOString(),
      };
      const messagesWithPlaceholder = [...baseMessages, placeholder];
      setMessages(messagesWithPlaceholder);

      // ── Fetch ──────────────────────────────────────────────────────────
      const controller = new AbortController();
      abortRef.current = controller;
      let stopped = false;
      try {
        const response = await apiSendMessage({
          sessionId,
          userQuery,
          stream:      true,
          attachment:  attachments?.[0],
          attachments,
          mode,
          signal:      controller.signal,
        });

        const contentType = response.headers.get("content-type") ?? "";
        const isStreaming = contentType.includes("text/event-stream");

        let finalContent  = "";
        let tokenUsage:   Message["token_usage"] | undefined;
        let suggestions:  string[] = [];
        let attachment:   { name: string } | undefined;

        if (isStreaming && response.body) {
          const reader  = response.body.getReader();
          const decoder = new TextDecoder();
          let accumulatedContent = "";

          try {
            outerLoop: while (true) {
              let read: ReadableStreamReadResult<Uint8Array>;
              try {
                read = await reader.read();
              } catch (readErr) {
                // AbortController fired — stop streaming, keep what we have.
                if (isAbortError(readErr)) {
                  stopped = true;
                  break outerLoop;
                }
                throw readErr;
              }

              const { done, value } = read;
              if (done) break;

              const chunk = decoder.decode(value, { stream: true });
              const lines = chunk.split("\n");

              for (const line of lines) {
                if (!line.startsWith("data: ")) continue;
                const data = line.slice(6);
                if (data === "[DONE]") break outerLoop;

                try {
                  const parsed = JSON.parse(data);

                  if (parsed.type === "content" && parsed.text) {
                    accumulatedContent += parsed.text;
                    const snap = accumulatedContent;
                    setMessages((prev) => {
                      const last = prev[prev.length - 1];
                      // Bail out (same array reference) when this stream's
                      // placeholder is not the current tail — prevents the
                      // render churn that trips "Maximum update depth" when
                      // a stale/overlapping stream tries to write.
                      if (!last || last.id !== assistantId || last.content === snap) return prev;
                      const updated = [...prev.slice(0, -1), { ...last, content: snap }];
                      return updated;
                    });
                  } else if (parsed.type === "done") {
                    tokenUsage  = parsed.usage;
                    suggestions = parsed.suggested_prompts ?? [];
                    attachment  = parsed.attachment;
                  } else if (parsed.type === "error") {
                    console.error("Stream error:", parsed.message);
                  }
                } catch {
                  // Malformed SSE line — skip
                }
              }
            }
          } finally {
            reader.releaseLock();
          }

          finalContent = accumulatedContent;
        } else {
          const data   = await response.json();
          finalContent = data.message ?? "";
          tokenUsage   = data.token_usage;
          suggestions  = data.suggested_prompts ?? [];
          attachment   = data.attachment;
        }

        const finalAssistant: Message = {
          id:        assistantId,
          role:      "assistant",
          content:   finalContent,
          timestamp: new Date().toISOString(),
          ...(suggestions.length ? { suggestions } : {}),
          ...(tokenUsage        ? { token_usage: tokenUsage } : {}),
          ...(attachment?.name  ? {
              attachments: [{ id: Date.now().toString(), name: attachment.name, type: "file" as const, url: "" }],
            }
          : {}),
        };

        const finalMessages = messagesWithPlaceholder.map((m) =>
          m.id === assistantId ? finalAssistant : m
        );
        setMessages(finalMessages);

        // Stopped before a single byte arrived — revert to the pre-request state.
        if (stopped && finalContent.length === 0) {
          setMessages(snapshotMessages);
          return;
        }

        const firstUserMsg = finalMessages.find((m) => m.role === "user");
        const title = firstUserMsg?.content
          ? firstUserMsg.content.replace(/<[^>]*>/g, "").substring(0, 50)
          : "New conversation";

        const lastMsg = finalMessages[finalMessages.length - 1];
        const lastMessage = lastMsg?.content
          ? lastMsg.content.replace(/<[^>]*>/g, "").substring(0, 100) + "..."
          : "";

        const tokensUsed = getTotalTokens(finalMessages);

        await updateSession(sessionId, {
          title,
          lastMessage,
          tokensUsed,
          messages: finalMessages,
        });

        // Admin analytics — log messages
        if (user) {
          logAskMessageSent(user.uid, sessionId, tokensUsed, (attachments?.length ?? 0) > 0);
          if (tokenUsage) {
            logAskMessageReceived(user.uid, sessionId, {
              input_tokens: tokenUsage.input_tokens,
              output_tokens: tokenUsage.output_tokens,
              total_tokens: tokenUsage.total_tokens,
            });
          }
        }

        if (isNewConversation && typeof window !== "undefined") {
          window.history.replaceState(null, "", `/ask/${sessionId}`);
        }
      } catch (err) {
        if (isAbortError(err)) {
          // User pressed Stop while connecting — just reset, no error shown.
          setMessages(snapshotMessages);
        } else {
          setMessages(snapshotMessages);
          const msg = err instanceof Error ? err.message : "Failed to get response. Please try again.";
          onError(msg);
        }
      } finally {
        abortRef.current = null;
        requestInFlightRef.current = false;
        setIsLoading(false);
      }
    },
    [user]
  );

  // ── regenerateMessage ────────────────────────────────────────────────────
  // Edit-and-regenerate: replaces the edited user message in place, drops
  // everything after it, persists the truncation, then re-runs the request
  // using the edited question as the last user message.

  const regenerateMessage = useCallback(
    async (editedMessage: Message) => {
      if (!user) return;
      if (requestInFlightRef.current) return;
      const sessionId = sessionIdRef.current;
      if (!sessionId) return;

      setError(null);
      const history = messagesRef.current;
      const idx = history.findIndex((m) => m.id === editedMessage.id);
      if (idx === -1) return;

      const truncated = history
        .slice(0, idx + 1)
        .map((m) => (m.id === editedMessage.id ? { ...m, content: editedMessage.content } : m));

      setMessages(truncated);

      const plain = (s: string) => s.replace(/<[^>]*>/g, "").trim();
      const firstUserMsg = truncated.find((m) => m.role === "user");
      const lastMsg = truncated[truncated.length - 1];

      await updateSession(sessionId, {
        messages: truncated,
        tokensUsed: getTotalTokens(truncated),
        title: (firstUserMsg?.content && plain(firstUserMsg.content).substring(0, 50)) || "New conversation",
        lastMessage: lastMsg?.content ? plain(lastMsg.content).substring(0, 100) : "",
      });

      await runAssistantRequest({
        sessionId,
        userQuery: editedMessage.content,
        attachments: undefined,
        baseMessages: truncated,
        snapshotMessages: truncated,
        mode: "fast",
        isNewConversation: false,
        onError: (msg) => {
          setError({ message: msg, retry: () => regenerateMessage(editedMessage) });
        },
      });
    },
    [user, runAssistantRequest]
  );

  // ── sendMessage ───────────────────────────────────────────────────────────

  const sendMessage = useCallback(
    async (content: string, attachments?: File[], mode: "fast" | "expert" = "fast") => {
      if (!user) throw new Error("User not authenticated");
      if (requestInFlightRef.current) return;

      setError(null);
      const plan       = (profile?.subscription as unknown as { plan?: string })?.plan;
      const tokenLimit = tokenLimitForPlan(plan);
      const isPro      = isPaidProfile(profile?.subscription);

      // Token limit check — session completed
      if (getTotalTokens(messages) >= tokenLimit) {
        const completedMsg: Message = {
          id:        `assistant-session-completed-${Date.now()}`,
          role:      "assistant",
          type:      "session_completed",
          content:   isPro
            ? `Session completed — you've used all ${tokenLimit.toLocaleString()} tokens for this conversation. A new conversation is starting automatically…`
            : `Session completed — you've used your free ${tokenLimit.toLocaleString()}-token session. Upgrade to Pro for 10× more tokens per conversation. A new conversation is starting automatically…`,
          timestamp: new Date().toISOString(),
        };
        const updated = [...messages, completedMsg];
        setMessages(updated);
        if (sessionIdRef.current) {
          await updateSession(sessionIdRef.current, {
            messages: updated,
            lastMessage: completedMsg.content.substring(0, 100),
          });
        }
        // Auto-continue: open a fresh conversation in the same window and
        // re-submit the question they just typed there, so generation picks up
        // without another press of Enter. Tokens then count in the NEW session.
        const scheduledFor = sessionIdRef.current;
        const carried      = content.replace(/<br\s*\/?>/gi, "\n");
        sessionResetTimerRef.current = window.setTimeout(() => {
          // Skip if the user already opened a different conversation meanwhile.
          if (sessionIdRef.current !== scheduledFor) return;
          if (isPro || !sessionLimitReached) {
            autoSendPendingRef.current = carried;
            setMessages([]);
            setSessionId("");
            onAutoNewChat?.();
          } else {
            // Free user at the 5-conversation cap — surface the limit modal.
            setShowSessionLimit(true);
          }
        }, 2000);
        return;
      }

      // ── Build user message ─────────────────────────────────────────────
      const userMessage: Message = {
        id:        `user-${Date.now()}-${Math.random().toString(36).slice(2)}`,
        role:      "user",
        content,
        timestamp: new Date().toISOString(),
        ...(attachments?.length
          ? {
              attachments: attachments.map((f, i) => ({
                id:   `${Date.now()}-${i}`,
                name: f.name,
                type: "file" as const,
                url:  "",
              })),
            }
          : {}),
      };

      // ── Create session if new ──────────────────────────────────────────
      let currentSessionId = sessionIdRef.current;
      let isNewConversation = false;

      if (!currentSessionId) {
        // Check session limit for free users
        let limitCheck: { allowed: boolean; sessionsUsed: number; sessionsLimit: number; retryAfter?: string } = { allowed: true, sessionsUsed: 0, sessionsLimit: 5 };

        if (!isPro) {
          limitCheck = await canCreateSession(user.uid);
          setSessionsUsed(limitCheck.sessionsUsed);
          setSessionsLimit(limitCheck.sessionsLimit);

          if (!limitCheck.allowed) {
            setSessionLimitReached(true);
            setShowSessionLimit(true);
            return;
          }
        }

        const session = await createSession(
          user.uid,
          "New conversation",
          plan,
          userMessage
        );
        currentSessionId = session.id;
        setSessionId(session.id);
        sessionIdRef.current = session.id;
        isNewConversation = true;

        // Admin analytics — log new session
        logAskSessionStarted(user.uid, session.id);

        // Update session count after creation
        if (!isPro) {
          const newCount = limitCheck.sessionsUsed + 1;
          setSessionsUsed(newCount);
          if (newCount >= limitCheck.sessionsLimit) {
            setSessionLimitReached(true);
            setShowSessionLimit(true);
          }
        }
      }
// ── Optimistic UI ─────────────────────────────────────────────────
      const messagesWithUser = [...messages, userMessage];
      setMessages(messagesWithUser);

      await runAssistantRequest({
        sessionId:        currentSessionId,
        userQuery:        content,
        attachments,
        baseMessages:     messagesWithUser,
        snapshotMessages: messages,
        mode,
        isNewConversation,
        onError: (msg) => {
          const retryFn = () => sendMessage(content, attachments, mode);
          setError({ message: msg, retry: retryFn });
        },
      });
    },
    [messages, user, profile, sessionLimitReached, runAssistantRequest, onAutoNewChat]
  );

  // ── Auto-continue after a completed session ────────────────────────────────
  // Once the reset above has run (messages are empty), re-submit the carried
  // question so the NEW session starts generating immediately.

  useEffect(() => {
    if (!autoSendPendingRef.current) return;
    if (messages.length > 0 || isLoading) return;
    const text = autoSendPendingRef.current;
    autoSendPendingRef.current = null;
    void sendMessage(text.replace(/\n/g, "<br>"));
  }, [messages, isLoading, sendMessage]);

  return {
    messages,
    setMessages,
    isLoading,
    error,
    sessionId,
    sendMessage,
    regenerateMessage,
    loadConversation,
    clearError,
    clearSession,
    stop,
    sessionsUsed,
    sessionsLimit,
    sessionLimitReached,
    showSessionLimit,
    setShowSessionLimit,
  };
}
